/**
 * Whether changing a completed report's figures could touch money that has
 * already been billed — the answer behind the warning in the confirm dialog.
 *
 * Fetched only while `enabled` (the dialog is open), so ordinary page views
 * make no extra request. Read from the Backoffice's billing records; when it
 * cannot answer the server falls back to an estimate, which `status.source`
 * says. `status` is null until loaded, or if the Hub itself could not be
 * reached — the dialog then falls back to a general caution.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { ReportBillingStatus } from '@/types/commercial-change';

export interface UseReportBillingStatusResult {
  status: ReportBillingStatus | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useReportBillingStatus(
  reportId: string | null,
  enabled: boolean
): UseReportBillingStatusResult {
  const [status, setStatus] = useState<ReportBillingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const latestRequest = useRef(0);

  const refetch = useCallback(async () => {
    const request = ++latestRequest.current;
    if (!reportId || !enabled) {
      setStatus(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.getReportBillingStatus(reportId);
      if (request === latestRequest.current) {
        setStatus(result);
      }
    } catch (err) {
      if (request !== latestRequest.current) {
        return;
      }
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setStatus(null);
      setError(e);
    } finally {
      if (request === latestRequest.current) {
        setIsLoading(false);
      }
    }
  }, [reportId, enabled]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { status, isLoading, error, refetch };
}
